import { useQueryClient } from "@tanstack/react-query";
import { Button, List, Typography } from "antd";
import { useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader } from "~Common/Components";
import Page from "~Common/Page";
import { useUserUpdates } from "~context/UserUpdatesContext/UserUpdatesContext.tsx";
import { NotificationStatuses } from "~enums/notificationStatuses.ts";
import NotificationItem from "~pages/Notifications/components/NotificationItem.tsx";
import { useGetNotifications } from "~pages/Notifications/hooks/useGetNotifications.ts";
import { useMarkAsReadNotifications } from "~pages/Notifications/hooks/useMarkAsReadNotifications.ts";

const NotificationsPage = () => {
  const { formatMessage } = useIntl();
  const { data: notifications, refetch: refetchNotifications, isPending } = useGetNotifications();
  const { refetch: refetchUserUpdates } = useUserUpdates();

  const unreadNotifications = notifications?.filter((notification) => notification.unread) || [];
  const restNotifications = notifications?.filter((notification) => !notification.unread) || [];
  const inProgressNotifications = restNotifications.filter((notification) =>
    [NotificationStatuses.New, NotificationStatuses.InProgress].includes(notification.status as NotificationStatuses),
  );
  const viewedNotifications = restNotifications.filter(
    (notification) => ![NotificationStatuses.New, NotificationStatuses.InProgress].includes(notification.status as NotificationStatuses),
  );
  const navigate = useNavigate();

  const onRefreshButtonClicked = async () => {
    await Promise.all([refetchNotifications(), refetchUserUpdates()]);
  };

  const { mutateAsync: markAllAsRead, isPending: isMarkAllAsReadIsPending } = useMarkAsReadNotifications();

  const queryClient = useQueryClient();

  const location = useLocation();
  const locationState = location?.state;
  const from = locationState?.from;

  return (
    <Page
      onBack={() => {
        queryClient.invalidateQueries({ queryKey: ["user", "updates"] });
        navigate(from || -1, { replace: true });
      }}
    >
      <div style={{ display: "flex", alignItems: "center" }}>
        <Typography.Title style={{ marginBottom: 0 }} level={4}>
          {formatMessage({ id: "notificationsPage.title" })}
        </Typography.Title>
        <Button
          style={{ marginLeft: "5px" }}
          onClick={() => onRefreshButtonClicked()}
          type={"text"}
          size={"small"}
          title={formatMessage({ id: "notificationsPage.button.refresh" })}
          icon={<i className={"fas fa-arrows-rotate"} />}
        />
      </div>
      {isPending ? (
        <Loader />
      ) : (
        <>
          {unreadNotifications.length > 0 && (
            <List
              header={
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  {formatMessage({ id: "unread.notifications" })}
                  <Button
                    loading={isMarkAllAsReadIsPending}
                    type="primary"
                    onClick={() => {
                      markAllAsRead();
                    }}
                  >
                    {formatMessage({ id: "mark.as.read.all" })}
                  </Button>
                </div>
              }
              itemLayout="vertical"
              size="large"
              dataSource={unreadNotifications}
              renderItem={(notification) => (
                <List.Item style={{ backgroundColor: "#EBEBEB" }} key={notification.key}>
                  <NotificationItem notification={notification} />
                </List.Item>
              )}
            />
          )}
          {inProgressNotifications.length > 0 && (
            <List
              header={formatMessage({ id: "inprogress.notifications" })}
              itemLayout="vertical"
              size="large"
              dataSource={inProgressNotifications}
              renderItem={(notification) => (
                <List.Item key={notification.key}>
                  <NotificationItem notification={notification} />
                </List.Item>
              )}
            />
          )}
          {viewedNotifications.length > 0 && (
            <List
              header={formatMessage({ id: "viewed.notifications" })}
              itemLayout="vertical"
              size="large"
              dataSource={viewedNotifications}
              renderItem={(notification) => (
                <List.Item key={notification.key}>
                  <NotificationItem notification={notification} />
                </List.Item>
              )}
            />
          )}
        </>
      )}
    </Page>
  );
};

export default NotificationsPage;
